
import React from 'react';
import { Content } from '@/types/content';
import { cn } from '@/lib/utils';
import { useVideoPreview } from '@/hooks/useVideoPreview';
import { VideoPlayer } from './VideoPlayer';
import { PlayButton } from './PlayButton';
import { useIsMobile } from '@/hooks/use-mobile';
interface VideoPreviewCardProps {
  video: Content;
  aspectRatio?: string;
  className?: string;
  previewDelay?: number;
  previewLoading?: 'lazy' | 'eager';
  autoplayOnLoad?: boolean;
  previewDuration?: number; // Duration in seconds to load for preview
}
const VideoPreviewCard: React.FC<VideoPreviewCardProps> = ({
  video,
  aspectRatio = "16/9",
  className,
  previewDelay = 100,
  previewLoading = 'eager',
  autoplayOnLoad = false,
  previewDuration = 30
}) => {
  const isMobile = useIsMobile();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = React.useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = React.useState(false); // NEW: track iframe load

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsNearViewport(entry.isIntersecting);
      },
      { root: null, rootMargin: '400px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const ensurePreconnect = (href: string) => {
    if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  };

  const warmCDN = () => {
    if (video.vimeoId) {
      ensurePreconnect('https://player.vimeo.com');
      ensurePreconnect('https://i.vimeocdn.com');
      ensurePreconnect('https://f.vimeocdn.com');
    }
  };

  const {
    isHovering,
    showPreview,
    isPreloaded,
    handleMouseEnter,
    handleMouseLeave
  } = useVideoPreview({
    previewDelay,
    isPreloadEnabled: !isMobile && isNearViewport,
    autoplayOnLoad
  });

  return <div className={cn("", className)}>
      
      <div
        ref={containerRef}
        className="relative"
        style={{
          aspectRatio
        }}
        onMouseEnter={() => { 
          warmCDN(); 
          setIsPreviewLoaded(false); // NEW: reset before showing preview
          handleMouseEnter(); 
        }}
        onMouseLeave={() => {
          handleMouseLeave();
          setIsPreviewLoaded(false); // NEW: reset when leaving
        }}
        onTouchStart={() => {
          warmCDN();
          setIsPreviewLoaded(false); // NEW
        }}
      >
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          {/* Hidden Preloader - only when allowed by hook */}
          {isPreloaded && video.vimeoId && !showPreview && <div className="hidden">
              <VideoPlayer vimeoId={video.vimeoId} autoplay={false} startTime={0} preload="metadata" lazyLoad={true} className="w-full h-full" />
            </div>}

          {/* Video Preview */}
          {showPreview && video.vimeoId && <div className="absolute inset-0 w-full h-full">
              <VideoPlayer 
                vimeoId={video.vimeoId} 
                autoplay={true} 
                startTime={0} 
                preload="auto" 
                lazyLoad={false} 
                className="w-full h-full" 
                onIframeLoad={() => setIsPreviewLoaded(true)}
                previewDuration={previewDuration}
              />
            </div>}

          {/* Thumbnail with Fallback */}
          <img 
            src={video.thumbnailUrl || video.thumbnail || 'https://images.unsplash.com/photo-1605810230434-7631ac76ec81'} 
            alt={video.title} 
            className={cn(
              "w-full h-full object-cover transition-opacity duration-300",
              showPreview && video.vimeoId && isPreviewLoaded ? "opacity-0" : "opacity-100" // NEW: keep visible until iframe load
            )} 
            loading={previewLoading} 
          />

          {/* Play Button */}
          <PlayButton isVisible={true} />
        </div>
      </div>
    </div>;
};
export default VideoPreviewCard;
